import { HouseholdError } from './householdError'

/**
 * What a rejected row mutation should actually say.
 *
 * Both household rows used to discard the server's rejection entirely and
 * render "…could not be changed. Try again in a moment." (issue 359). That is
 * wrong for every rejection these endpoints actually produce: none of them is
 * transient, and retrying is precisely what will not help. A 403 on a role
 * change means *your own role changed under you*, where the honest recovery is
 * to reload, not to retry.
 *
 * `docs/rules.md` calls the "something went wrong" shape a defect wherever
 * something better is available. Here something better is available, because
 * the server already said what was wrong.
 *
 * ## Why this is only ever reached in a race
 *
 * The UI pre-empts all three role rejections before the request — that is what
 * `canChangeRole`, `isSelf` and `isLastOwner` are for. So the only way to see
 * this copy is a genuine race: the state changed between render and click.
 * That is exactly the case where the user's mental model is stale and the
 * specific message is worth the most.
 *
 * ## The strings ARE the contract, and NOTHING IN THIS REPO CAN DEFEND THEM
 *
 * The backend answers with FastAPI's default `{"detail": "..."}` envelope and
 * no structured error code, so there is nothing else to match on. **A backend
 * copy change silently reverts this file**: the mapping stops matching, every
 * rejection falls to the generic arm, and the user is told to retry the three
 * things retrying cannot fix — with no test failing anywhere.
 *
 * **`rowFailure.test.ts` does NOT catch that, and an earlier version of this
 * paragraph claimed it did** (web-architect gate, blocking finding 1). That
 * test builds its `detail` strings from the same literals this file matches,
 * so it is our own constant asserted back to ourselves; a rewording leaves it
 * green. It is a real test of the MAPPING — which arm each input takes, and
 * that unknown input falls through rather than failing closed — and it is not
 * a contract test. Saying otherwise was worse than saying nothing, because it
 * told the next reader the risk was covered.
 *
 * The strings are true as of this writing, verified against
 * `../expense-monitor-backend/api/src/services/household_service.py:364,375,381`
 * — the sibling checkout is the only authority; they appear in no fixture and
 * in no generated schema. When this changes, that file is where to look.
 *
 * The real defence is an e2e assertion against a live rejection from the
 * running backend, which is the tier that can see the actual wire response.
 * Until one exists, treat this mapping as unguarded against upstream copy
 * changes.
 *
 * If the backend ever grows real error codes, match on those and delete the
 * string arms — the generic arm is what makes that safe to do incrementally.
 *
 * The same shape as `AcceptInvitation.tsx`'s own `classify`, and local to this
 * directory rather than shared for the same reason `InviteDialog`'s
 * `fieldMessageFor`/`dialogMessageFor` are local: per-surface message
 * functions are already this repo's pattern, and the copy is not reusable even
 * where the status codes are.
 */

/** The body FastAPI sends by default, read defensively — the same narrowing
 *  `AcceptInvitation.tsx` does, for the same envelope.
 *
 *  Deliberately narrow to a STRING `detail`: the role-change and resend
 *  endpoints this file reads answer with FastAPI's bare `HTTPException`
 *  envelope, never the array `{ detail: ValidationError[] }` a 422 sends
 *  (`src/api/validationError.ts`). If either endpoint ever grows real body
 *  validation, the fix is to route that response through
 *  `routeValidationErrors`, the same as the three forms already do — not to
 *  widen this function to also accept an array, which would blur the two
 *  envelopes this file exists to keep apart. */
function detailOf(body: unknown): string {
  if (typeof body !== 'object' || body === null || !('detail' in body)) return ''
  const { detail } = body as { detail: unknown }
  return typeof detail === 'string' ? detail : ''
}

/**
 * Why a role change was refused, as something worth reading.
 *
 * `label` is the member the change was aimed at, already resolved by the
 * caller. Falls through to the generic arm for anything unrecognised — the
 * mapping must never become a second place that fails closed on an unknown
 * string, which is the failure mode that would make this card a regression
 * rather than a fix.
 */
export function describeRoleFailure(label: string, cause: unknown): string {
  if (!(cause instanceof HouseholdError)) {
    return `${label}'s role could not be changed. Try again in a moment.`
  }
  const detail = detailOf(cause.body)

  // Not "you are not an owner" — the control only renders for an owner, so
  // reaching a 403 means the viewer's OWN role changed since this screen
  // loaded. Reloading is the recovery; retrying is not.
  if (cause.status === 403 || detail === 'Only household owners can change member roles') {
    return 'Your role in this household changed, so you can no longer change roles. Reload to see where things stand.'
  }
  if (detail === 'Cannot change your own role') {
    return 'You cannot change your own role. Ask another owner to do it.'
  }
  /**
   * Defensive — issue 389. **The server cannot currently send this rejection on
   * this endpoint**, and the arm is kept anyway.
   *
   * `household_service.py:378-381` raises it only when the TARGET is an owner
   * and `_count_owners` returns 1 or less. Reaching that line needs two earlier
   * guards to have passed: the requester is an owner
   * (`:364`, else 403) and the target is not the requester (`:375`, else
   * "Cannot change your own role"). Together those mean the requester and the
   * target are two DISTINCT owners, so the count is at least 2 and `:381` can
   * never fire. No fixture can provoke it; neither can a race, because the
   * contradiction is inside one request's own guards rather than between them
   * and the client's.
   *
   * The `ACTIVE` filter does not open a hole, which is the one way the
   * arithmetic could have failed: `_count_owners` (`:567`) counts only ACTIVE
   * owner memberships, and `get_user_membership` — which both earlier guards
   * read — filters on ACTIVE too. So the requester is always inside the count
   * they are being compared against.
   *
   * **Not to be confused with its two reachable siblings**, which differ by one
   * word and belong to other endpoints: `Cannot remove the last owner`
   * (`:443` — reachable, since an ADMIN may remove a sole owner and is not
   * themselves an owner) and `Cannot leave as last owner. Transfer ownership or
   * delete household first.` (`:489`).
   *
   * Kept rather than deleted: if those guards are ever reordered or the
   * self-change check relaxed, the mapping is already correct, and the generic
   * arm below is what makes carrying an unreachable arm free.
   */
  if (detail === 'Cannot demote the last owner') {
    return `${label} is the last owner, so their role cannot be changed until someone else is made an owner.`
  }
  // 5xx and a bare network failure (no status to read) are the cases where
  // "try again" is honest, so the generic arm keeps that wording.
  return `${label}'s role could not be changed. Try again in a moment.`
}

/**
 * Why a resend was refused.
 *
 * Narrower than the role mapping because the server's own guards here are
 * fewer, but the same rule applies: a 403 is about the viewer's role having
 * changed, not about the invitation, and it is not transient.
 *
 * **Enumerated, not passed through** (web-architect gate, N3). An earlier
 * version returned the server's own `detail` verbatim for any sub-500 status
 * it did not recognise, on the argument that the server's copy is specific
 * and already user-facing. That made this the only place in the app putting
 * an arbitrary backend string in front of a user: the set is unbounded, the
 * length uncapped, the tone unreviewed, and no test can pin an arm that
 * echoes its input. It was also inconsistent with `describeRoleFailure`
 * twelve lines up, which enumerates and never echoes.
 *
 * The set turned out to be knowable — `invitation_service.py:509,522,525`
 * raises exactly three sub-500 rejections — so enumerating cost nothing that
 * passing through was buying.
 */
export function describeResendFailure(cause: unknown): string {
  if (!(cause instanceof HouseholdError)) {
    return 'The invitation could not be resent. Try again in a moment.'
  }
  const detail = detailOf(cause.body)

  if (cause.status === 403) {
    return 'Your role in this household changed, so you can no longer resend invitations. Reload to see where things stand.'
  }
  if (cause.status === 404) {
    return 'This invitation no longer exists. Reload to see the current list.'
  }
  // `f"Cannot resend invitation with status {status}"` — the one 400 this
  // endpoint raises, where `status` is accepted/revoked/expired. Matched with
  // a pattern rather than three literals because the server builds it by
  // interpolation, so the enumerable part is the STATUS, not the sentence.
  const settled = CANNOT_RESEND_STATUS.exec(detail)
  if (settled) {
    const state = settled[1]
    return state === 'accepted'
      ? 'That invitation has already been accepted. Reload to see the current list.'
      : `That invitation is ${state}, so it cannot be resent. Revoke it and send a new one.`
  }
  return 'The invitation could not be resent. Try again in a moment.'
}

/**
 * The status inside the resend endpoint's one 400.
 *
 * Anchored at both ends: an unanchored pattern matches any string that merely
 * CONTAINS this sentence, so a junk `detail` carrying it as a substring would
 * take this arm. Verified against
 * `../expense-monitor-backend/api/src/services/invitation_service.py:525-528`.
 */
const CANNOT_RESEND_STATUS = /^Cannot resend invitation with status (\w+)$/
